import React, { useState, useContext, useMemo } from 'react';
import { Button, Table, Form, InputGroup, Pagination, Badge, Container } from 'react-bootstrap';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { ToastContainer, toast, Zoom } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { MdOutlineRestore, MdSearch, MdUnfoldMore, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdEdit } from "react-icons/md";
import { RiResetLeftFill } from "react-icons/ri";
import Swal from 'sweetalert2';
import { FaRegThumbsUp, FaRegThumbsDown } from "react-icons/fa";

// Context & API
import api from '../../api';
import { Datacontext } from '../../context/dataContext';
import { Dimcontext } from '../../context/pagecontext';
import { AuthContext } from '../../context/authContext';

// Components
import Header from '../topbotm/Header';
import Footer from '../topbotm/Footer';
import Sidebar from '../dashboard/sidebar'; 
import { Editnl } from '../edit/editNl';

// Styles
import 'react-circular-progressbar/dist/styles.css';
import '../css/pors/doc_process.css'; 

const MyNl = () => {
  const { setDim } = useContext(Dimcontext);
  const { user } = useContext(AuthContext);
  const { nls, fetchNl, loading } = useContext(Datacontext);
  const [edit, set_Edit] = useState(false);

  // --- UI & Pagination States ---
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'application_no', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState(null); 

  // --- Modal States ---
  const [docid, set_docid] = useState('');

  const showsToast = (msg) => toast.success(msg, { position: "bottom-center", autoClose: 2000, theme: "dark", transition: Zoom });

  // --- Data Filtering & Sorting Logic ---
  const processedData = useMemo(() => {
    if (!nls) return [];
    let filtered = nls.filter(d => d.docstatus !== "processing");
    
    // Filter by User Role
    if (user?.role === "user") {
      filtered = filtered.filter(d => 
        d?.managed_By?.map(name => name.toLowerCase()).includes(user.name?.toLowerCase())
      );
    }
    
    // Filter by Approved/Rejected Status
    if (statusFilter) filtered = filtered.filter(d => d.docstatus === statusFilter);
    
    // Filter by Search Term
    if (searchTerm) {
      filtered = filtered.filter(doc => {
        const name = doc?.owner?.name || "";
        const searchStr = `${name} ${doc.application_no} ${doc.register_office}`.toLowerCase();
        return searchStr.includes(searchTerm.toLowerCase());
      });
    }

    // Advanced Sorting (Handling Nested Objects)
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.key === 'name') {
          aValue = a?.owner?.name || "";
          bValue = b?.owner?.name || "";
        } else if (sortConfig.key === 'status') {
          aValue = a.status?.status || "";
          bValue = b.status?.status || "";
        } else {
          aValue = a[sortConfig.key] || "";
          bValue = b[sortConfig.key] || "";
        }

        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();

        if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [nls, user, searchTerm, sortConfig, statusFilter]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = processedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(processedData.length / rowsPerPage);

  const toggleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleReset = () => {
    setSearchTerm('');
    setStatusFilter(null);
    setCurrentPage(1);
    setSortConfig({ key: 'application_no', direction: 'asc' });
  };

  const handleRestore = async (id) => {
  try {
    // 1. Fire the confirmation alert
    const result = await Swal.fire({
      title: 'Restore Application?',
      text: "Are you sure you want to move this document back to Processing?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#8a2be2', // Matches your theme
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Restore it!'
    });

    // 2. Only proceed if the user clicked "Yes"
    if (result.isConfirmed) {
      await api.patch(`/updateone_nl/${id}`, { data1: { docstatus: "processing" } });
      showsToast("Restored to Processing");
      fetchNl();
    }
  } catch (error) { 
    console.error("Restore failed", error); 
    // Optional: Add an error toast here if you have one configured
    // toast.error("Failed to restore document");
  }
};

  const calculateProgress = (doc) => {
  if (!doc) return 0;

  // Step 1: Application & Executive Details (5 Fields)
  // Matching your isStep1Complete logic
  const step1Fields = [
    !!doc.zone, // zoneId
    !!doc.register_office, // officeId
    doc.managed_By && doc.managed_By.length > 0, // users1
    !!doc.refered_by, // ref
    !!doc.owner // owner
  ];

  // Step 2: Document Process
  const isOnline = doc.status?.status?.toLowerCase() === 'online';

  let hasPrimaryDocDetails = false;
  if (isOnline) {
    // Online: Valid if nlno is 'no' OR ('yes' and has a number)
    hasPrimaryDocDetails = 
      doc.nlno?.value === 'no' || 
      (doc.nlno?.value === 'yes' && !!doc.nlno?.no);
  } else {
    // Offline: Requires nlno value, valid payment, and valid stamp paper
    const nlnoValid = !!doc.nlno?.value;
    const payValid = doc.payment?.value === 'no' || (doc.payment?.value === 'yes' && !!doc.payment?.val);
    const stampValid = doc.stamp_paper?.value === 'no' || (doc.stamp_paper?.value === 'yes' && !!doc.stamp_paper?.val);
    
    hasPrimaryDocDetails = nlnoValid && payValid && stampValid;
  }

  const step2Fields = [
    hasPrimaryDocDetails,
    !!doc.nl_received?.value,
    !!doc.nl_delivered?.value,
    !!doc.estimated_cost
  ];

  // Combine all fields
  const allFields = [...step1Fields, ...step2Fields];
  const completedCount = allFields.filter(Boolean).length;
  const totalCount = allFields.length;

  const percentage = Math.floor((completedCount / totalCount) * 100);
  
  // Return at least 10% if it's a valid record to show some progress in the UI
  return percentage === 0 ? 10 : percentage;
};

  const getBadgeColor = (name) => {
    const variants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info'];
    return variants[(name?.length || 0) % variants.length];
  };

  const handleEdit = (id) => {
    setDim(true);
    set_docid(id);
    set_Edit(true);
  };

  return (
    <div className="dashboard-page">
      <Header />
      <ToastContainer />
      
      <div className="dashboard-wrapper">
        <div className='d-none' style={{backgroundColor:"#4a127e"}}>
          <Sidebar user={user} />
        </div>

        <main className="main-area">
          <Container fluid="xxl" className="py-4 px-3 px-md-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }} 
              animate={{ opacity: 1, scale: 1 }} 
              className="main-glass-card shadow-sm border"
            >
              {/* Header Section */}
              <div className="card-header-custom d-flex flex-wrap justify-content-between align-items-center p-3 gap-3">
                <h4 className="m-0 fw-bold text-dark">Nagal History</h4>
                <div className="d-flex gap-3 align-items-center">
                  {(searchTerm || statusFilter) && (
                    <Button variant="link" className="p-0 text-decoration-none text-danger d-flex align-items-center gap-1" onClick={handleReset}>
                      <RiResetLeftFill size={18} /> Clear
                    </Button>
                  )}
                  <InputGroup className="search-bar" style={{ width: '250px' }}>
                    <InputGroup.Text className="bg-white"><MdSearch size={20} /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search records..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                  </InputGroup>
                </div>
              </div>

              {/* Table Container */}
              <div className="table-side-scroll-container">
                <Table hover striped responsive className="imtable mb-0">
                  <thead>
                    <tr>
                      <th className="sticky-col pin-no">NO.</th>
                      <th className="sticky-col pin-name cursor-pointer" onClick={() => toggleSort('name')}>
                        OWNER <MdUnfoldMore />
                      </th>
                      <th className="cursor-pointer text-center" onClick={() => toggleSort('application_no')}>
                        APP NO <MdUnfoldMore />
                      </th>
                      <th className="cursor-pointer text-center" onClick={() => toggleSort('type')}>
                        TYPE <MdUnfoldMore />
                      </th>
                      <th>MANAGED BY</th>
                      <th className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          STATUS
                          <FaRegThumbsUp 
                            className={`cursor-pointer ${statusFilter === 'completed' ? 'text-success' : 'text-white'}`} 
                            onClick={() => { setStatusFilter(statusFilter === 'completed' ? null : 'completed'); setCurrentPage(1); }}
                          />
                          <FaRegThumbsDown 
                            className={`cursor-pointer ${statusFilter === 'rejected' ? 'text-danger' : 'text-white'}`} 
                            onClick={() => { setStatusFilter(statusFilter === 'rejected' ? null : 'rejected'); setCurrentPage(1); }} 
                          />
                        </div>
                      </th>
                      <th className="text-center">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="text-center py-5"><div className="custom-loader mx-auto"></div></td></tr>
                    ) : currentRows.length > 0 ? (
                      currentRows.map((doc, index) => (
                        <tr key={doc.id || index} className="align-middle">
                          <td className="sticky-col pin-no">{indexOfFirstRow + index + 1}</td>
                          <td className="sticky-col pin-name fw-semibold">{doc?.owner?.name?.toUpperCase()}</td>
                          <td><Badge bg="light" text="dark" className="border">{doc.application_no}</Badge></td>
                          
                          {/* TYPE Badge */}
                          <td>
                            <div className="d-flex justify-content-center">
                              <span 
                                className={`badge rounded-pill bg-${doc?.status?.status?.toLowerCase() === 'online' ? 'success' : 'secondary'}`} 
                                style={{ fontSize: '0.60rem', minWidth: '55px' }}
                              >
                                {doc?.status?.status?.toUpperCase() || "N/A"}
                              </span>
                            </div>
                          </td>

                          {/* Managed By Column */}
                          <td>
                            <div className="d-flex flex-wrap gap-1 justify-content-center">
                              {doc.managed_By?.map((m, i) => (
                                <span key={i} className={`badge rounded-pill bg-${getBadgeColor(m)}`} style={{ fontSize: '0.6rem' }}>
                                  {m?.toUpperCase()}
                                </span>
                              ))}
                            </div>
                          </td>

                          {/* Status / Progress Ring Column */}
                          <td className="text-center">
                            {doc.docstatus === 'rejected' ? (
                              <div className="d-flex flex-column align-items-center">
                                <div style={{ width: 35, height: 35 }}>
                                  <CircularProgressbar
                                    value={calculateProgress(doc)} text={`${calculateProgress(doc)}%`}
                                    styles={buildStyles({ textSize: '30px', pathColor: '#dc3545', textColor: '#dc3545' })}
                                  />
                                </div>
                                <small className="text-danger fw-bold mt-1" style={{ fontSize: '9px' }}>{doc.rejreason || 'Rejected'}</small>
                              </div>
                            ) : (
                              <Badge pill bg="success">Completed</Badge>
                            )}
                          </td>

                          {/* Action Column */}
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {doc.docstatus === "rejected" && user?.role === "admin" && (
                                <Button variant="outline-primary" size="sm" onClick={() => handleRestore(doc.id)}>
                                  <MdOutlineRestore size={18} />
                                </Button>
                              )}
                              {user?.role === "admin" && (
                                <Button className="btn-action br" onClick={() => handleEdit(doc.id)}><MdEdit /></Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={7} className="text-center py-5 text-muted">No history records found.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Footer */}
              <div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Rows per page:</span>
                  <Form.Select 
                    size="sm" 
                    style={{ width: '80px', borderRadius: '6px' }}
                    value={rowsPerPage} 
                    onChange={(e) => { 
                      setRowsPerPage(Number(e.target.value)); 
                      setCurrentPage(1); 
                    }}
                  >
                    {[10, 20, 50, 100].map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </Form.Select>
                </div>

                <div className="text-muted small fw-medium">
                  {processedData.length > 0 ? (
                    <>Showing <b>{indexOfFirstRow + 1}</b> to <b>{Math.min(indexOfLastRow, processedData.length)}</b> of <b>{processedData.length}</b> entries</>
                  ) : "No entries found"}
                </div>

                <Pagination className="mb-0 custom-pagination shadow-sm">
                  <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
                  <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                    <MdKeyboardArrowLeft size={20} />
                  </Pagination.Prev>
                  <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>
                  <Pagination.Next disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                    <MdKeyboardArrowRight size={20} />
                  </Pagination.Next>
                  <Pagination.Last disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(totalPages)} />
                </Pagination>
              </div>
            </motion.div>
          </Container>
        </main>
      </div>

      {/* Modal Overlay Section */}
      <AnimatePresence>
        {edit && (
          <motion.div
            key="edit-modal"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="modal-overlay-wrapper"
          >
            <Editnl set_docedit={set_Edit} id={docid} showToast={toast.warn} onclose={fetchNl} />
          </motion.div>
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
};

export default MyNl;